import { useFeatureContext } from '@/contexts/FeatureContext';

interface FeatureData {
  hasFeature: boolean;
  featureLimit: number;
  currentUsage: number;
  remainingUsage: number;
  canUseFeature: boolean;
  isLoading: boolean;
  error?: Error | null;
  refetch?: () => void;
}

/**
 * Hook to check if the current user's organization has access to a specific feature
 * Now uses the centralized FeatureContext for optimal performance
 * @param featureKey - The key of the feature to check
 * @returns Object containing feature access information
 */
export function useFeature(featureKey: string): FeatureData {
  const { 
    getFeature, 
    hasFeature, 
    canUseFeature,
    getFeatureLimit,
    getFeatureUsage,
    getRemainingUsage,
    isLoading, error, refetch
  } = useFeatureContext();

  // Get the feature data from context (no API call!)
  const feature = getFeature(featureKey);

  // Return data in the same format as before for backward compatibility
  return {
    hasFeature: feature?.hasFeature || hasFeature(featureKey),
    featureLimit: feature?.featureLimit || getFeatureLimit(featureKey),
    currentUsage: feature?.currentUsage || getFeatureUsage(featureKey),
    remainingUsage: feature?.remainingUsage || getRemainingUsage(featureKey),
    canUseFeature: feature?.canUseFeature || canUseFeature(featureKey),
    isLoading, error, refetch,
  };
}

/**
 * Hook to check multiple features at once
 * Now uses the centralized FeatureContext for optimal performance
 * @param featureKeys - Array of feature keys to check
 * @returns Object with feature keys as properties containing feature data
 */
export function useFeatures(featureKeys: string[]): Record<string, FeatureData> & { isLoading: boolean } {
  const { getFeature, isLoading } = useFeatureContext();

  const results: Record<string, FeatureData> = {};

  // Get all features from context (no API calls!)
  featureKeys.forEach((featureKey) => {
    const feature = getFeature(featureKey);
    results[featureKey] = {
      hasFeature: feature?.hasFeature || false,
      featureLimit: feature?.featureLimit || 0,
      currentUsage: feature?.currentUsage || 0,
      remainingUsage: feature?.remainingUsage || 0,
      canUseFeature: feature?.canUseFeature || false,
      isLoading: false,
    };
  });

  return {
    ...results,
    isLoading,
  };
}